// Goaround transactional email design system.
// 8 tokens, 7 components. Every template is built from these primitives.
//
// All output is inline-styled HTML for cross-client compatibility
// (Gmail / Outlook web / Apple Mail / Superhuman).

import { escapeHtml } from './format.ts';

export const tokens = {
  ink: '#1a1614',
  inkSoft: '#6b665f',
  amber: '#ed7a1f',
  pop: '#f0c020',
  wine: '#7c2a26',
  cream: '#f3eee5',
  card: '#fbf6ec',
  cardAmber: '#f6e8d4',
  cardMint: '#dfe9dc',
  cardPink: '#f5dcd9',
  line: '#e6e0d4',
  white: '#ffffff',
};

const fontStack = `'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif`;
const displayStack = `'Bricolage Grotesque', 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif`;
const serifStack = `'Instrument Serif', Georgia, 'Times New Roman', serif`;
const monoStack = `ui-monospace, 'SF Mono', 'Roboto Mono', Menlo, Monaco, Consolas, monospace`;

// ─── Wordmark ─────────────────────────────────────────────────────────────
// Always paired with the amber period.
export function renderWordmark(): string {
  return `<span style="font-family:${displayStack};font-weight:800;font-size:24px;letter-spacing:-0.02em;color:${tokens.ink};">Goaround<span style="color:${tokens.amber};">.</span></span>`;
}

// ─── Status dot ───────────────────────────────────────────────────────────
// Sets the tone in the eyebrow. One per email.
export type StatusTone = 'action' | 'heads-up' | 'confirmed' | 'cancelled' | 'neutral';
const dotColors: Record<StatusTone, string> = {
  action: tokens.amber,
  'heads-up': tokens.amber,
  confirmed: '#3a8a4e',
  cancelled: '#b03a3a',
  neutral: tokens.inkSoft,
};
export function renderStatusDot(label: string, tone: StatusTone = 'neutral'): string {
  return `<div style="font-family:${monoStack};font-size:11px;letter-spacing:0.16em;text-transform:uppercase;color:${tokens.ink};margin:0 0 18px;">
    <span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${dotColors[tone]};vertical-align:1px;margin-right:8px;"></span>${escapeHtml(label)}
  </div>`;
}

// ─── Display heading ──────────────────────────────────────────────────────
// Bricolage Grotesque, with optional Instrument Serif italic accent.
// Usage: renderHeading("Locked in. The tab's", 'on us', 'to handle.')
export function renderHeading(before: string, accent?: string, after?: string): string {
  const accentSpan = accent
    ? `<em style="font-family:${serifStack};font-style:italic;font-weight:400;color:${tokens.wine};letter-spacing:-0.01em;"> ${escapeHtml(accent)}</em>`
    : '';
  const afterText = after ? ` ${escapeHtml(after)}` : '';
  return `<h1 style="font-family:${displayStack};font-weight:800;font-size:34px;line-height:1.05;letter-spacing:-0.035em;color:${tokens.ink};margin:0 0 18px;text-wrap:balance;">${escapeHtml(before)}${accentSpan}${afterText}</h1>`;
}

// ─── Body paragraph ───────────────────────────────────────────────────────
export function renderParagraph(html: string, opts: { muted?: boolean } = {}): string {
  const color = opts.muted ? tokens.inkSoft : tokens.ink;
  return `<p style="font-family:${fontStack};font-size:15px;line-height:1.55;color:${color};margin:0 0 16px;">${html}</p>`;
}

// ─── Info card ────────────────────────────────────────────────────────────
// Anchors every booking email. Cream by default; recolor for state.
export type InfoCardTone = 'amber' | 'mint' | 'pink';
const cardBgs: Record<InfoCardTone, string> = {
  amber: tokens.cardAmber,
  mint: tokens.cardMint,
  pink: tokens.cardPink,
};
export type InfoRow = { label: string; value: string };
export function renderInfoCard(rows: InfoRow[], opts: { tone?: InfoCardTone; image?: string } = {}): string {
  const tone = opts.tone ?? 'amber';
  const bg = cardBgs[tone];
  const image = opts.image
    ? `<img src="${escapeHtml(opts.image)}" alt="" width="100%" style="display:block;width:100%;max-width:100%;height:auto;border-radius:10px 10px 0 0;" />`
    : '';
  const rowsHtml = rows
    .map(
      (r) => `<tr>
        <td style="padding:8px 0;vertical-align:top;width:34%;">
          <div style="font-family:${monoStack};font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:${tokens.inkSoft};">${escapeHtml(r.label)}</div>
        </td>
        <td style="padding:8px 0;vertical-align:top;">
          <div style="font-family:${fontStack};font-size:15px;line-height:1.45;color:${tokens.ink};">${r.value}</div>
        </td>
      </tr>`,
    )
    .join('');
  return `<div style="margin:0 0 24px;">
    ${image}
    <div style="background:${bg};border-radius:${opts.image ? '0 0 12px 12px' : '12px'};padding:18px 22px;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%" role="presentation" style="border-collapse:collapse;">${rowsHtml}</table>
    </div>
  </div>`;
}

// ─── Stat row ─────────────────────────────────────────────────────────────
// Three big-number tiles (used in vendor weekly + headcount lock).
export type Stat = { label: string; value: string; sub?: string };
export function renderStatRow(stats: Stat[]): string {
  const w = Math.floor(100 / stats.length);
  const cells = stats
    .map(
      (s) => `<td valign="top" style="width:${w}%;padding:18px 20px;">
        <div style="font-family:${monoStack};font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:${tokens.inkSoft};margin-bottom:6px;">${escapeHtml(s.label)}</div>
        <div style="font-family:${displayStack};font-size:28px;font-weight:800;line-height:1;color:${tokens.ink};letter-spacing:-0.02em;">${escapeHtml(s.value)}</div>
        ${s.sub ? `<div style="font-family:${fontStack};font-size:13px;color:${tokens.inkSoft};margin-top:6px;">${escapeHtml(s.sub)}</div>` : ''}
      </td>`,
    )
    .join('');
  return `<table cellpadding="0" cellspacing="0" border="0" width="100%" role="presentation" style="border-collapse:collapse;background:${tokens.cardAmber};border-radius:12px;margin:0 0 24px;"><tr>${cells}</tr></table>`;
}

// ─── Buttons ──────────────────────────────────────────────────────────────
// Pill shape. One primary per email max.
export type ButtonStyle = 'primary' | 'pop' | 'ghost';
export function renderButton(label: string, href: string, style: ButtonStyle = 'primary'): string {
  const styles: Record<ButtonStyle, string> = {
    primary: `background:${tokens.ink};color:${tokens.white};border:1.5px solid ${tokens.ink};`,
    pop: `background:${tokens.amber};color:${tokens.ink};border:1.5px solid ${tokens.amber};`,
    ghost: `background:${tokens.white};color:${tokens.ink};border:1.5px solid ${tokens.ink};`,
  };
  return `<a href="${escapeHtml(href)}" style="display:inline-block;${styles[style]}padding:13px 22px;border-radius:999px;font-family:${fontStack};font-weight:600;font-size:15px;text-decoration:none;line-height:1;white-space:nowrap;">${escapeHtml(label)}</a>`;
}

export type ButtonSpec = { label: string; href: string; style?: ButtonStyle };
export function renderButtonRow(buttons: ButtonSpec[]): string {
  const cells = buttons
    .map((b) => `<td style="padding:0 10px 0 0;">${renderButton(b.label, b.href, b.style ?? 'ghost')}</td>`)
    .join('');
  return `<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse;margin:0 0 24px;"><tr>${cells}</tr></table>`;
}

// ─── RSVP cluster ─────────────────────────────────────────────────────────
// Three big buttons. Yes is the only primary.
export function renderRsvpCluster(opts: {
  yesUrl: string;
  maybeUrl: string;
  noUrl: string;
  yesLabel?: string;
  maybeLabel?: string;
  noLabel?: string;
  yesStyle?: ButtonStyle;
}): string {
  return renderButtonRow([
    { label: opts.yesLabel ?? "I'm in 🥂", href: opts.yesUrl, style: opts.yesStyle ?? 'pop' },
    { label: opts.maybeLabel ?? 'Maybe', href: opts.maybeUrl, style: 'ghost' },
    { label: opts.noLabel ?? "Can't make it", href: opts.noUrl, style: 'ghost' },
  ]);
}

// ─── Callout ──────────────────────────────────────────────────────────────
// Tinted note for "Try again with a Friday" / "Quick math" callouts.
// Distinguished by amber left border.
export function renderCallout(html: string): string {
  return `<div style="background:${tokens.cardAmber};border-left:3px solid ${tokens.amber};border-radius:6px;padding:14px 18px;margin:0 0 24px;font-family:${fontStack};font-size:14px;line-height:1.5;color:${tokens.ink};">${html}</div>`;
}

// ─── Footer ───────────────────────────────────────────────────────────────
// Wordmark on the left, status pill on the right.
const pillBg: Record<string, string> = {
  TRANSACTIONAL: tokens.cardAmber,
  CANCELLATION: tokens.cardPink,
  ENGAGEMENT: tokens.cardAmber,
  INVITE: tokens.cardAmber,
  CONFIRMED: tokens.cardMint,
  CANCELLED: tokens.cardPink,
  REMINDER: tokens.cardAmber,
  NUDGE: tokens.cardAmber,
  'LAST CALL': tokens.cardPink,
  BOOKING: tokens.cardMint,
  'DAY-OF': tokens.cardAmber,
  WEEKLY: tokens.cardAmber,
  AUTH: tokens.cardAmber,
  ONBOARDING: tokens.cardMint,
  TEAM: tokens.cardAmber,
};
export function renderFooter(opts: { tagline: string; pill: string; unsubscribeUrl?: string }): string {
  const bg = pillBg[opts.pill.toUpperCase()] ?? tokens.cardAmber;
  const unsub = opts.unsubscribeUrl
    ? ` &middot; <a href="${escapeHtml(opts.unsubscribeUrl)}" style="color:${tokens.inkSoft};text-decoration:underline;">Unsubscribe</a>`
    : '';
  return `<table cellpadding="0" cellspacing="0" border="0" width="100%" role="presentation" style="border-top:1px solid ${tokens.line};margin-top:8px;border-collapse:collapse;"><tr>
    <td style="padding:22px 0 4px;vertical-align:middle;">
      <div style="margin-bottom:4px;">${renderWordmark()}</div>
      <div style="font-family:${fontStack};font-size:12px;color:${tokens.inkSoft};">${escapeHtml(opts.tagline)}${unsub}</div>
    </td>
    <td align="right" style="padding:22px 0 4px;vertical-align:middle;">
      <span style="display:inline-block;background:${bg};border-radius:999px;padding:6px 14px;font-family:${monoStack};font-size:10px;letter-spacing:0.16em;text-transform:uppercase;color:${tokens.ink};">${escapeHtml(opts.pill)}</span>
    </td>
  </tr></table>`;
}

// ─── Shell ────────────────────────────────────────────────────────────────
// The outer chrome every email shares: cream backdrop, white card,
// wordmark header with optional right-side label.
export function renderShell(opts: {
  preheader: string;
  headerLabel?: string;
  body: string;
  footer: string;
}): string {
  const headerLabel = opts.headerLabel
    ? `<td align="right" style="vertical-align:middle;"><div style="font-family:${monoStack};font-size:11px;letter-spacing:0.16em;text-transform:uppercase;color:${tokens.inkSoft};">${escapeHtml(opts.headerLabel)}</div></td>`
    : '';
  return `<!DOCTYPE html><html lang="en"><head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="x-apple-disable-message-reformatting">
<meta name="color-scheme" content="light only">
<meta name="supported-color-schemes" content="light only">
<title>Goaround</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@800&family=DM+Sans:wght@400;500;600;700&family=Instrument+Serif:ital@1&display=swap');
  body{margin:0;padding:0;background:${tokens.cream};}
  a{color:${tokens.ink};}
  @media (max-width:600px){
    .ga-card{padding:24px 20px !important;}
    .ga-outer{padding:16px 12px !important;}
  }
</style>
</head><body style="margin:0;padding:0;background:${tokens.cream};font-family:${fontStack};">
<div style="display:none;font-size:1px;line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;mso-hide:all;">${escapeHtml(opts.preheader)}</div>
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:${tokens.cream};border-collapse:collapse;">
<tr><td align="center" class="ga-outer" style="padding:32px 16px;">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width:600px;background:${tokens.card};border-radius:14px;border-collapse:collapse;">
    <tr><td class="ga-card" style="padding:32px 36px;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;margin-bottom:24px;border-bottom:1px solid ${tokens.line};padding-bottom:24px;"><tr>
        <td style="vertical-align:middle;">${renderWordmark()}</td>
        ${headerLabel}
      </tr></table>
      ${opts.body}
      ${opts.footer}
    </td></tr>
  </table>
</td></tr>
</table>
</body></html>`;
}

// ─── Subject helper ───────────────────────────────────────────────────────
// Strip newlines / control chars — Resend rejects them.
export function cleanSubject(s: string): string {
  return s.replace(/[\r\n\t]+/g, ' ').trim();
}
